<template>
  <div>
    <advanced-form
      autocomplete="off"
      submit-label="Save Email"
      :form="formFields"
      v-bind="form"
      v-model="form"
      @submit="input()"
    >

      <b-alert
        v-if="!Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length"
        slot="from-before"
        variant="warning"
        show
      >
        It seems like no email addresses have been connected to Real Links yet. In order to send slack messages on behalf
        of you, you need to go to <a :href="$router.resolve({ name: 'EmailSetup' }).href" target="_blank">Settings > Connect Email</a> page.
      </b-alert>

      <div slot="body-after" class="text-muted mb-3">
        <b-card no-body class="mb-1">
          <b-card-header class="d-flex justify-content-between align-items-center bg-white">
            <div @click="displayVariables = !displayVariables" v-b-toggle.available-variables-card>
              <strong>Template variables you can use</strong>
            </div>
            <b-button @click="displayVariables = !displayVariables"  v-b-toggle.available-variables-card variant="outline-primary" class="px-2" size="sm">
              <font-awesome-icon v-if="displayVariables" icon="chevron-up" size="sm"/>
              <font-awesome-icon v-else icon="chevron-down" size="sm"/>
            </b-button>
          </b-card-header>
          <b-collapse id="available-variables-card" accordion="available-variables-card" role="tabpanel" class="p-3">
            <b>User related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'USER')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            <b>Job related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'JOB')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            <b>Leaderboard related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'LEADERBOARD')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
          </b-collapse>
        </b-card>
      </div>
    </advanced-form>
    <!-- Test Email Form -->
    <h3
      class="py-3"
    >
      <strong>Message Preview</strong>
    </h3>
    <b-form>
      <!-- Email Addresses Field -->
      <test-message-form
        :body="form.body"
        :from="form.from"
        :job="form.job"
        :contentShare="form.contentShare"
        :isIndividual="isIndividual"
      />
    </b-form>

    <!-- ALERT MODALS -->
    <b-modal id="target-group-change" title="Target group has changed" ok-only ok-title="I Understand">
      <div v-if="isIndividual">
        <p>This slack message will be sent as a direct message to everyone in the selected group.</p>
      </div>
      <div v-else>
        <p>This slack message will be sent as a group message to everyone in the selected group.</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import TestMessageForm from '@src/components/campaign-admin/test-message-form.vue';
import gql from 'graphql-tag';

const SendTestEmailsMutation = gql`
  mutation sendTestCampaignEmails($input: sendTestEmails!) {
    sendTestCampaignEmails(input: $input)
  }
`;

export default {
  props: {
    campaignID: {
      type: String,
    },
    value: {
      type: Object,
    },
    syncedEmailAddresses: {
      type: Array,
    }
  },
  components: {
    AdvancedForm,
    Share,
    TestMessageForm,
  },
  data() {
    const { slackMessage } = this.value;
    console.log({ slackMessage });
    delete slackMessage.__typename;
    return {
      form: {
        title: '',
        from: null,
        to: 'ALL_USERS',
        sendAt: '',
        body: '',
        status: 'DRAFT',
        ...(slackMessage || {})
      },
      testEmails: null,
      displayVariables: false,
    };
  },
  mixins: [
    gridMixin,
  ],
  watch: {
    isIndividual() {
      this.$bvModal.show('target-group-change');
    }
  },
  computed: {
    toChoices() {
      const { isIndividual } = this;
      return {
        ALL_USERS: `All Users (Participants & Invited Users) - ${isIndividual ? 'as a direct slack message' : 'in a group'}`,
        PARTICIPANTS: `Participants (Users who already joined the campaign) - ${isIndividual ? 'as a direct slack message' : 'in a group'}`,
        INVITED_USERS: `Invited Users (Users who have not joined the campaign yet) - ${isIndividual ? 'as a direct slack message' : 'in a group'}`,
      };
    },
    formFields() {
      const { syncedEmailAddresses, campaignID } = this;
      console.log({ campaignID });
      const defaultEmailOption = { NONE: '---- Select an email address ----' };
      return {
        title: {
          label: 'Title',
          type: 'STRING',
          helpText: 'This is displayed for you only'
        },
        from: {
          type: 'SELECT',
          label: 'From',
          disabled: !Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length,
          choices: Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length
            ? syncedEmailAddresses.reduce((out, { emailAddress, fullName }) => {
              out[emailAddress] = `${emailAddress} (${fullName})`;
              return out;
            }, defaultEmailOption) : [ defaultEmailOption ],
          helpText: 'The person this message will be delieverd on behalf of'
        },
        to: {
          label: 'To',
          type: 'SELECT',
          required: true,
          choices: this.toChoices,
          helpText: 'The target group of this slack message'
        },
        sendAt: {
          label: 'Send At',
          placeholder: 'e.g. 2020-12-01 13:00',
          helpText: 'The time when this slack message will be sent, in your timezone',
          type: 'DATETIME',
        },
        ...(this.isJob ? {
          job: {
            label: 'Jobs',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: {
                status: 'ACTIVE',
                campaigns: {
                  id: campaignID,
                }
              },
              many: false,
              order: 'CREATED_DESC',
            },
            helpText: 'Select the job you want to include in the slack message',
          },
        } : {}),
        ...(this.isContent ? {
          contentShare: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: false,
              where: {
                status: 'ACTIVE',
                campaigns: {
                  id: campaignID,
                }
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select the piece of content you want to include in the slack message',
          },
        } : {}),
        body: {
          label: 'Content',
          placeholder: 'Hi Joe, ...',
          type: 'HTML',
        },
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          choices: {
            READY: 'Ready',
            DRAFT: 'Draft',
          },
        },
      };
    },
    usedVariables() {
      const { body } = this.form;
      return [ ...new Set((body || '').split('{{').reduce((r, el) => {
        if (!el.includes('}}')) return r;
        const [ variable ] = el.split('}}');
        r.push(variable);
        return r;
      }, [])) ];
    },
    variables() {
      return [
        {
          key: 'JOB_TITLE',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Title of the selected job.',
        }, {
          key: 'JOB_SHARE_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'The link of the job users can share. This is uniquelly generated for every user.',
        }, {
          key: 'JOB_DESCRIPTION',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected job.',
        }, {
          key: 'JOB_SHARE_TEXT',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the job.',
        }, {
          key: 'JOB_MATCH_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'LinkedIn network page of the user with possible matches',
        }, {
          key: 'CONTENT_TITLE',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Title of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_LINK',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'The link of the piece of content users can share. This is uniquelly generated for every user.',
        }, {
          key: 'CONTENT_DESCRIPTION',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_TEXT',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the piece of content.',
        }, {
          key: 'LEADERBOARD_FIRST_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the first prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the second prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the third prize of the leaderboard.',
        }, {
          key: 'FIRST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The first name of the current user on the leaderboard.',
        }, {
          key: 'LAST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The last name of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POSITION',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The position of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POINTS',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The amount of points/tokens the current user has collected.',
        },
      ];
    },
    isIndividual() {
      const keys = this.variables.filter(({ target }) => target === 'INDIVIDUAL').map(({ key }) => key);
      return !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    isJob() {
      const keys = this.variables.filter(({ type }) => type === 'JOB').map(({ key }) => key);
      return !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    isContent() {
      const keys = this.variables.filter(({ type }) => type === 'CONTENT').map(({ key }) => key);
      return !!keys.filter(key => this.usedVariables.includes(key)).length;
    }
  },
  methods: {
    input() {
      const component = { slackMessage: this.form };
      if (this.form.job) {
        delete this.form.job.customFields;
        delete this.form.job.location;
        delete this.form.job.createdAt;
        delete this.form.job.status;
        delete this.form.job.department;
        delete this.form.job.__typename;
      }
      console.log({ form: this.form });
      this.$emit('input', component);
    },
    async sendTestEmails() {
      try {
        const result = await this.$apollo.mutate({
          mutation: SendTestEmailsMutation,
          variables: {
            input: {
              componentID: this.value.id,
              testRecipients: this.testEmails,
            }
          },
        });

        if (result === 'ERROR') {
          this.$notifyErr('Failed to send test messages.');
        } else {
          this.$notification({
            title: 'Email sent',
            message: 'Email message has been sent successfully.',
            variant: 'success',
          });
        }
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr('Failed to save post times');
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-slack-message.vue